import { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { useReviewMedia } from '@/hooks/useReviewMedia';
import ReviewMediaLightbox from './media/ReviewMediaLightbox';
import { storageThumbUrl } from '@/lib/imageThumb';
import { Skeleton } from '@/components/ui/skeleton';


interface ReviewMediaGalleryProps {
  reviewId: string;
  compact?: boolean;
}

const VideoPreview = ({
  thumbnailUrl,
  mediaUrl,
  alt,
  className,
}: {
  thumbnailUrl?: string | null;
  mediaUrl: string;
  alt: string;
  className: string;
}) => {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const [fallbackToVideo, setFallbackToVideo] = useState(false);
  const [generatedThumbUrl, setGeneratedThumbUrl] = useState<string | null>(null);
  const generatedUrlRef = useRef<string | null>(null);

  const canUseStoredThumb = Boolean(thumbnailUrl) && !thumbnailFailed;

  useEffect(() => {
    if (canUseStoredThumb || fallbackToVideo || generatedThumbUrl) return;

    let cancelled = false;

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = mediaUrl;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';

    const cleanup = () => {
      video.removeEventListener('error', onError);
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('seeked', onSeeked);
      try {
        video.removeAttribute('src');
        video.load();
      } catch {
        // ignore
      }
    };

    const hardFail = () => {
      cleanup();
      if (!cancelled) setFallbackToVideo(true);
    };

    const onError = () => hardFail();

    const onLoadedData = () => {
      try {
        const targetTime = Math.min(0.12, Math.max(0, (video.duration || 0) - 0.12));
        video.currentTime = targetTime;
      } catch {
        hardFail();
      }
    };

    const onSeeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 1;
        canvas.height = video.videoHeight || 1;
        const ctx = canvas.getContext('2d');
        if (!ctx) return hardFail();

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            cleanup();
            if (cancelled || !blob) return hardFail();

            // Revoke any previous generated URL
            if (generatedUrlRef.current) {
              URL.revokeObjectURL(generatedUrlRef.current);
            }

            const url = URL.createObjectURL(blob);
            generatedUrlRef.current = url;
            setGeneratedThumbUrl(url);
          },
          'image/jpeg',
          0.82
        );
      } catch {
        hardFail();
      }
    };

    video.addEventListener('error', onError);
    video.addEventListener('loadeddata', onLoadedData);
    video.addEventListener('seeked', onSeeked);

    const timeout = window.setTimeout(() => {
      hardFail();
    }, 6000);

    try {
      video.load();
    } catch {
      // ignore
    }

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      cleanup();
      if (generatedUrlRef.current) {
        URL.revokeObjectURL(generatedUrlRef.current);
        generatedUrlRef.current = null;
      }
    };
  }, [canUseStoredThumb, fallbackToVideo, generatedThumbUrl, mediaUrl]);

  if (canUseStoredThumb) {
    return (
      <img
        src={storageThumbUrl(thumbnailUrl!, { width: 192 })}
        alt={alt}
        className={className}
        loading="lazy"
        decoding="async"
        crossOrigin="anonymous"
        onLoad={(e) => {
          const img = e.currentTarget;
          try {
            const w = Math.min(32, img.naturalWidth || 0);
            const h = Math.min(32, img.naturalHeight || 0);
            if (!w || !h) return;

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            ctx.drawImage(img, 0, 0, w, h);
            const data = ctx.getImageData(0, 0, w, h).data;

            let sum = 0;
            let sumSq = 0;
            let count = 0;

            // Sample every 4th pixel to keep it cheap
            for (let i = 0; i < data.length; i += 16) {
              const lum = (data[i] + data[i + 1] + data[i + 2]) / 3;
              sum += lum;
              sumSq += lum * lum;
              count++;
            }

            const mean = sum / Math.max(1, count);
            const variance = sumSq / Math.max(1, count) - mean * mean;
            const isUniform = variance < 6;
            const isMostlyBlank = mean > 245 || mean < 10 || (mean > 120 && mean < 140);

            // If the thumbnail is basically a flat white/gray/black tile, treat as broken.
            if (isUniform && isMostlyBlank) setThumbnailFailed(true);
          } catch {
            // If we can't inspect pixels (CORS-taint, etc.), fall back to generating from the video.
            setThumbnailFailed(true);
          }
        }}
        onError={() => setThumbnailFailed(true)}
      />
    );
  }

  if (generatedThumbUrl && !fallbackToVideo) {
    return (
      <img
        src={generatedThumbUrl}
        alt={alt}
        className={className}
        loading="lazy"
        decoding="async"
      />
    );
  }

  return (
    <video
      src={mediaUrl}
      className={className}
      preload="auto"
      muted
      playsInline
    />
  );
};

const ReviewMediaGallery = ({ reviewId, compact = false }: ReviewMediaGalleryProps) => {
  const { data: mediaItems = [], isLoading } = useReviewMedia(reviewId);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  if (isLoading) {
    // Compact cards live inside feeds where most reviews have no media: a
    // placeholder strip would be removed again for the majority of cards, so
    // reserving space there causes more movement than it prevents.
    if (compact) {
      return null;
    }

    return (
      <div className="flex gap-2 overflow-hidden pb-2" aria-hidden>
        <Skeleton className="h-32 w-32 shrink-0 rounded-lg" />
        <Skeleton className="h-32 w-48 shrink-0 rounded-lg" />
      </div>
    );
  }

  if (mediaItems.length === 0) {
    return null;
  }

  const photos = mediaItems.filter(m => m.media_type === 'photo');
  const videos = mediaItems.filter(m => m.media_type === 'video');

  const handleMediaClick = (index: number) => {
    setActiveIndex(index);
    setLightboxOpen(true);
  };

  const handlePrev = () => {
    setActiveIndex(prev => (prev > 0 ? prev - 1 : mediaItems.length - 1));
  };

  const handleNext = () => {
    setActiveIndex(prev => (prev < mediaItems.length - 1 ? prev + 1 : 0));
  };

  const activeMedia = mediaItems[activeIndex];

  if (compact) {
    // Compact view: show thumbnails inline
    return (
      <div className="flex gap-1 mt-2">
        {mediaItems.slice(0, 4).map((media, index) => (
          <button
            key={media.id}
            type="button"
            aria-label={media.media_type === 'photo' ? 'Open review photo' : 'Play review video'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleMediaClick(index);
            }}
            className="relative w-12 h-12 rounded overflow-hidden bg-muted flex-shrink-0"
          >
            {media.media_type === 'photo' ? (
              <img
                src={storageThumbUrl(media.media_url, { width: 48, height: 48 })}
                alt="Review photo"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="relative w-full h-full">
                <VideoPreview
                  thumbnailUrl={media.thumbnail_url}
                  mediaUrl={media.media_url}
                  alt="Review video thumbnail"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <Play className="h-4 w-4 text-white fill-white" />
                </div>
              </div>
            )}
            {index === 3 && mediaItems.length > 4 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-xs font-medium">
                +{mediaItems.length - 4}
              </div>
            )}
          </button>
        ))}

        {/* Lightbox */}
        <ReviewMediaLightbox
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
          media={activeMedia}
          onPrev={handlePrev}
          onNext={handleNext}
          currentIndex={activeIndex}
          totalCount={mediaItems.length}
        />
      </div>
    );
  }

  // Full gallery view - side by side horizontal scroll
  return (
    <div className="space-y-3">
      {/* Horizontal scroll container for all media */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-muted">
        {/* Photos */}
        {photos.map((photo) => (
          <button
            key={photo.id}
            type="button"
            aria-label="Open review photo"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleMediaClick(mediaItems.indexOf(photo));
            }}
            className="relative flex-shrink-0 w-32 h-32 rounded-lg overflow-hidden bg-muted hover:opacity-90 transition-opacity"
          >
            <img
              src={storageThumbUrl(photo.media_url, { width: 128, height: 128 })}
              alt="Review photo"
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </button>
        ))}

        {/* Videos - show thumbnail with play button overlay */}
        {videos.map((video) => (
          <button
            key={video.id}
            type="button"
            aria-label="Play review video"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleMediaClick(mediaItems.indexOf(video));
            }}
            className="relative flex-shrink-0 w-48 h-32 rounded-lg overflow-hidden bg-muted hover:opacity-90 transition-opacity"
          >
            <VideoPreview
              thumbnailUrl={video.thumbnail_url}
              mediaUrl={video.media_url}
              alt="Review video thumbnail"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <Play className="h-8 w-8 text-white fill-white" />
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox */}
      <ReviewMediaLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        media={activeMedia}
        onPrev={handlePrev}
        onNext={handleNext}
        currentIndex={activeIndex}
        totalCount={mediaItems.length}
      />
    </div>
  );
};

export default ReviewMediaGallery;
